import type { Repository } from '../types';
import { MAX_REPOS } from '../data';

const GITHUB_USERNAME = 'husky-696';

// Filter settings - edit these to control which repos show
const INCLUDE_PATTERNS: string[] = []; // Show all repos natively without hardcoded names
const EXCLUDE_PATTERNS = ['fork', 'temp', 'test', 'demo', 'old']; // Hide repos with these in name

const LANGUAGE_COLORS: Record<string, string> = {
  JavaScript: '#f1e05a',
  TypeScript: '#2b7489',
  Python: '#3572A5',
  HTML: '#e34c26',
  CSS: '#563d7c',
  Java: '#b07219',
  'C++': '#f34b7d',
  C: '#555555',
  Go: '#00ADD8',
  Rust: '#dea584',
  Ruby: '#701516',
  PHP: '#4F5D95',
  Swift: '#ffac45',
  Kotlin: '#A97BFF',
  Shell: '#89e051',
  Vue: '#41b883',
};

export function getLanguageColor(language: string | null): string {
  if (!language) return '#71717a';
  return LANGUAGE_COLORS[language] || '#71717a';
}

// Fallback repos when API is rate limited
const FALLBACK_REPOS: Repository[] = [];

// Check if repo matches filter criteria
function shouldShowRepo(repo: Repository): boolean {
  const nameLower = repo.name.toLowerCase();
  const descLower = (repo.description || '').toLowerCase();
  
  // Exclude if matches exclude patterns
  for (const pattern of EXCLUDE_PATTERNS) {
    if (nameLower.includes(pattern) || descLower.includes(pattern)) {
      return false;
    }
  }
  
  // Include if matches include patterns (or if no include patterns defined)
  if (INCLUDE_PATTERNS.length === 0) return true;
  
  for (const pattern of INCLUDE_PATTERNS) {
    if (nameLower.includes(pattern) || descLower.includes(pattern)) {
      return true;
    }
  }
  
  // Also check topics
  if (repo.topics) {
    for (const pattern of INCLUDE_PATTERNS) {
      if (repo.topics.some(t => t.toLowerCase().includes(pattern))) {
        return true;
      }
    }
  }
  
  return false;
}

// Fetch repos with filtering
export async function fetchRepos(): Promise<Repository[]> {
  try {
    const response = await fetch(`https://api.github.com/users/${GITHUB_USERNAME}/repos?per_page=100`);
    
    if (!response.ok) {
      console.warn('GitHub API rate limited or error, using fallback repos');
      return FALLBACK_REPOS;
    }
    
    const repos: Repository[] = await response.json();
    
    // Filter, sort by stars, limit
    const filtered = repos
      .filter(repo => !repo.fork)
      .filter(shouldShowRepo)
      .sort((a, b) => b.stargazers_count - a.stargazers_count)
      .slice(0, MAX_REPOS);
    
    console.log('Filtered repos:', filtered.map(r => r.name));
    return filtered.length > 0 ? filtered : FALLBACK_REPOS;
  } catch (error) {
    console.error('Failed to fetch repos:', error);
    return FALLBACK_REPOS;
  }
}

// Fetch a single repo from GitHub API using owner/repo format
export async function fetchRepoDetails(owner: string, repo: string): Promise<Repository | null> {
  try {
    const response = await fetch(`https://api.github.com/repos/${owner}/${repo}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        console.warn('Repository not found');
      } else if (response.status === 403) {
        console.warn('GitHub API rate limited');
      }
      return null;
    }
    
    const repoData: Repository = await response.json();
    return repoData;
  } catch (error) {
    console.error('Failed to fetch repo details:', error);
    return null;
  }
}

// Parse GitHub URL and fetch repo details
export async function fetchRepoFromUrl(url: string): Promise<Repository | null> {
  try {
    // Parse GitHub URL to extract owner and repo
    // Supports: https://github.com/owner/repo or https://github.com/owner/repo/
    const match = url.match(/github\.com\/([^/]+)\/([^/]+)/);
    if (!match) {
      console.warn('Invalid GitHub URL format');
      return null;
    }
    
    const [, owner, repo] = match;
    return await fetchRepoDetails(owner, repo);
  } catch (error) {
    console.error('Failed to parse/fetch repo from URL:', error);
    return null;
  }
}
